import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Detects that a newer frontend build was deployed while the tab was open,
 * by polling the build id stamped into assets/version.json (see
 * scripts/write-version.js, run from the Dockerfile before `ng build`).
 *
 * index.html is never cached (nginx.conf), but a tab that's been open since
 * before a deploy already has the old index.html/bundles in memory — the
 * only way it learns about a new build is by asking. Any deploy that
 * changes the dashboard UI updates this file; a backend-only deploy does
 * not, so it correctly stays silent.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly versionUrl = 'assets/version.json';
  private readonly pollIntervalMs = 10 * 60 * 1000; // 10 min

  private bootVersion: string | null = null;
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  private readonly updateAvailable$$ = new BehaviorSubject<boolean>(false);
  readonly updateAvailable$ = this.updateAvailable$$.asObservable();

  async start(): Promise<void> {
    this.bootVersion = await this.fetchVersion();
    if (!this.bootVersion) return; // couldn't read it (offline, blocked, dev quirk) — nothing to compare against

    this.pollHandle = setInterval(() => this.checkNow(), this.pollIntervalMs);
    // Users often leave a tab backgrounded for hours; check the moment they
    // come back instead of waiting for the next interval tick.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.checkNow();
    });
  }

  stop(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
    this.pollHandle = null;
  }

  private async checkNow(): Promise<void> {
    if (this.updateAvailable$$.value) return; // already flagged, no need to keep polling
    const latest = await this.fetchVersion();
    if (latest && this.bootVersion && latest !== this.bootVersion) {
      this.updateAvailable$$.next(true);
    }
  }

  private async fetchVersion(): Promise<string | null> {
    try {
      // Cache-buster query param: belt-and-braces alongside nginx's
      // Cache-Control on this path — a stale CDN/browser cache would
      // otherwise defeat the whole check.
      const res = await fetch(`${this.versionUrl}?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return null;
      const data = await res.json();
      return typeof data?.version === 'string' ? data.version : null;
    } catch {
      return null; // network hiccup — try again on the next poll, don't nag the user over it
    }
  }

  reload(): void {
    window.location.reload();
  }
}
